/**
 * Trouve les deux vrais axes locaux (dLon, dLat) de la grille d'un modèle à
 * un point donné, en sondant les points de grille voisins via GetFeatureInfo.
 *
 * Sonder seulement l'est vrai ne suffit PAS pour une grille tournée (HRDPS,
 * RDPS, RAQDPS) : rien ne garantit que cette direction arbitraire tombe sur
 * un vrai voisin adjacent plutôt que sur un voisin en diagonale
 * (accidentellement ~√2× plus loin et mal orienté). On détecte ce cas en
 * comparant la magnitude trouvée à une devinette de résolution (voir
 * fetchGridSpacing) : si elle est trop grande, on corrige en sondant à ±45°
 * de cette direction, où se trouve un vrai voisin — puisque dans toute
 * grille régulière, même tournée, un voisin en diagonale se situe
 * exactement à 45° entre les deux vrais axes adjacents. Le second axe est
 * ensuite simplement perpendiculaire au premier (les deux axes d'une grille
 * régulière sont toujours orthogonaux, même tournée).
 */

import { fetchFeatureInfo } from './wms.js';
import { fetchGridSpacing } from './wcs.js';

// Au-delà de ce ratio (magnitude trouvée / devinette de résolution), on
// soupçonne un voisin en diagonale (~√2 ≈ 1.41×) plutôt qu'un vrai voisin
// adjacent (~1.0-1.3× selon le bruit de mesure observé empiriquement).
const DIAGONAL_RATIO = 1.3;

function rotateDeg(vec, deg) {
  const r = deg * Math.PI / 180;
  return [
    vec[0] * Math.cos(r) - vec[1] * Math.sin(r),
    vec[0] * Math.sin(r) + vec[1] * Math.cos(r),
  ];
}

/**
 * @param {object} params
 * @param {string} params.layer   - couche WMS, ex: 'HRDPS.CONTINENTAL_TT'
 * @param {string} [params.style] - style WMS à forcer, cf. fetchFeatureInfo
 * @param {string} params.time    - instant ISO 8601, cf. fetchFeatureInfo
 * @param {string} params.refTime - origine de la prévision, cf. fetchFeatureInfo
 * @param {number} params.lon0    - longitude du point de grille central
 * @param {number} params.lat0    - latitude du point de grille central
 * @param {AbortSignal} [signal]
 * @returns {Promise<{axisU: [number, number], axisV: [number, number]}>}
 *   axisU/axisV : vecteurs [dLon, dLat] réels des deux axes de la grille,
 *   pas nécessairement est/nord — orthogonaux entre eux par construction.
 * @throws {Error} si aucun point de grille voisin distinct n'a pu être trouvé
 */
export async function findGridAxes({ layer, style, time, refTime, lon0, lat0 }, signal) {
  let guess;
  try {
    guess = await fetchGridSpacing(layer, signal);
  } catch (error) {
    console.error('Erreur DescribeCoverage:', error);
    guess = { lonStep: 0.1, latStep: 0.1 };
  }
  const refMag = Math.max(guess.lonStep, guess.latStep);

  // Sonde dans une direction donnée (vecteur non nécessairement unitaire)
  // en augmentant le pas jusqu'à trouver un point de grille distinct du
  // centre ; retourne le vecteur (dLon, dLat) complet vers ce voisin, ou
  // null si aucun n'a été trouvé.
  const probeDirection = async (dir) => {
    const norm = Math.hypot(dir[0], dir[1]);
    const unit = [dir[0] / norm, dir[1] / norm];
    let step = refMag;
    for (let attempt = 0; attempt < 8; attempt++) {
      const probeLon = lon0 + unit[0] * step;
      const probeLat = lat0 + unit[1] * step;
      const { gridLon, gridLat } = await fetchFeatureInfo(
        { layer, lat: probeLat, lon: probeLon, time, refTime, style },
        signal
      );
      if (gridLon !== null && (gridLon !== lon0 || gridLat !== lat0)) {
        return [gridLon - lon0, gridLat - lat0];
      }
      step *= 1.5;
    }
    return null;
  };

  let u = await probeDirection([1, 0]);
  if (u === null) {
    throw new Error('Aucun point de grille voisin trouvé (est)');
  }

  if (Math.hypot(...u) > DIAGONAL_RATIO * refMag) {
    const angleU = Math.atan2(u[1], u[0]) * 180 / Math.PI;
    const candidates = [u];
    const uPlus = await probeDirection(rotateDeg([1, 0], angleU + 45));
    if (uPlus !== null) candidates.push(uPlus);
    if (uPlus === null || Math.hypot(...uPlus) > DIAGONAL_RATIO * refMag) {
      const uMinus = await probeDirection(rotateDeg([1, 0], angleU - 45));
      if (uMinus !== null) candidates.push(uMinus);
    }
    u = candidates.reduce((best, c) => Math.hypot(...c) < Math.hypot(...best) ? c : best);
  }

  const v = await probeDirection([-u[1], u[0]]);
  if (v === null) {
    throw new Error('Aucun point de grille voisin trouvé (perpendiculaire)');
  }

  return { axisU: u, axisV: v };
}
